import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, effect, ElementRef, inject, input, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { CwInput, CwMenu, CwMenuItem } from '@bbraun/cortex-angular';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map, Observable, startWith, switchMap, tap } from 'rxjs';
import { CustomShadowCssDirective } from '~/app/directives/CustomShadowCss';
import { LabelType, PrintLabelPipe } from '~/app/pipes/PrintLabel';
import { UiStore } from '~/app/store/ui.store';
import { getItemByLabel } from '~/app/utils/getItemByLabel';
import { change } from '~/app/utils/valueChanges';

@Component({
  selector: 'gplus-autocomplete',
  imports: [
    CwMenu,
    CwInput,
    TranslateModule,
    AsyncPipe,
    CustomShadowCssDirective,
    PrintLabelPipe,
    NgTemplateOutlet,
    CwMenuItem
  ],
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [PrintLabelPipe]
})
export class AutocompleteComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly printLabelPipe = inject(PrintLabelPipe);
  private readonly elm = inject(ElementRef);
  private readonly uiStore = inject(UiStore);

  public control = input<AbstractControl>();
  public filteredBy = input<Array<string> | string>();
  public data = input<Array<any>>();
  public label = input<string>();
  public placeholder = input<string>();
  public required = input<boolean>();
  public disabled = input<boolean>();
  public readonly = input<boolean>();
  public attrVef = input<string>();
  public attrVefItem = input<string>();
  public validations = input<Record<string, any>>({});
  public labelType = input<LabelType>();
  public hideAssistiveOnNull = input<boolean>(false);

  public filteredData: Observable<Array<any>>;

  public inputTextValue = toSignal(
    toObservable(this.control).pipe(
      filter((control) => !!control),
      switchMap((control: AbstractControl) =>
        control.valueChanges.pipe(
          startWith(this.getInputTextValue(control.value)),
          map((value) => this.getInputTextValue(value)),
          tap(() => {
            if (this.control().pristine) {
              this.error.set('');
            } else {
              this.error.set(
                Object.keys(this.validations()).reduce((err, key) => {
                  return err || (this.control().hasError(key) ? this.validations()[key] : null);
                }, null)
              );
            }
          })
        )
      )
    ),
    { initialValue: '' }
  );

  public error = signal<string>(null);
  public width = computed<string>(() => {
    // we track changes in the screen size to update the width accordingly
    this.uiStore.windowSize();

    // to calculate again the width of the component
    const width = this.elm?.nativeElement?.offsetWidth;
    return width ? width + 'px' : null;
  });

  // Custom shadow dom styles
  public assistiveBlock = 'cw-input-assistive{display: block!important}';
  public assistiveNone = 'cw-input-assistive{display: none!important}';

  @ViewChild('autoCompleteMenu') menu: CwMenu;

  constructor() {
    effect(() => {
      if (this.data() && !this.filteredData) {
        this.filteredData = change(this.control(), this.data(), this.filteredBy()).pipe(
          takeUntilDestroyed(this.destroyRef)
        );
      }
    });
  }

  private getInputTextValue(value: Record<string, any> | string): string {
    return this.printLabelPipe.transform(value, typeof value === 'string' ? 'default' : this.labelType());
  }

  public selected(inputtedValue: any) {
    if (!this.control()) {
      return;
    }

    // Important, first mark the control as dirty before
    // setting the value so is updated when checking on changes
    this.control().markAsDirty();

    if (inputtedValue instanceof CustomEvent) {
      const valueAsString = inputtedValue.detail as string;
      const item = getItemByLabel(this.labelType(), valueAsString, this.data());
      this.control().setValue(item || valueAsString);
    } else {
      this.control().setValue(inputtedValue);
    }
  }
}
